import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Container,
    Row,
    Col,
    Card,
    Table,
    Button
} from "react-bootstrap";
import { RootState } from "../../../../index";
import { fetchChallanById } from "slices/sales_order/challan/thunk";
import { clearChallanState } from "slices/sales_order/challan/reducer";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import LoadingSpinner from "components/LoadingSpinner";
import { MecordColors } from "Constants/Color";
import { formatIndianAmount } from "helpers/formatIndianAmount";
import DOMPurify from "dompurify";
import InfoIcon from "@mui/icons-material/Info";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import ProductHistoryModal from "Common/Components/ProductHistoryModal/ProductHistoryModal";
import { toast } from "react-toastify";
import ChallanSplitSummary from "./components/ChallanSplitSummary";

const ViewChallan = () => {
    const { id: challanId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch<any>();

    const { challanDetail, loading } = useSelector((state: RootState) => state.Challan);

    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [selectedSerialNo, setSelectedSerialNo] = useState<string>("");
    const [productHistory, setProductHistory] = useState<any>(null);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const fetchProductHistory = async (serialNo: string) => {
        if (!serialNo || serialNo === "-") {
            toast.error("Invalid serial number");
            return;
        }

        setLoadingHistory(true);
        setSelectedSerialNo(serialNo);
        setShowHistoryModal(true);

        try {
            const response = await Axios.get(API_ENDPOINT.INVENTORY.PRODUCT_HISTORY(serialNo));
            setProductHistory(response.data?.data || response.data);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Failed to fetch product history");
            setProductHistory(null);
        } finally {
            setLoadingHistory(false);
        }
    };

    useEffect(() => {
        if (challanId) {
            dispatch(fetchChallanById(challanId));
        }
        return () => {
            dispatch(clearChallanState());
        };
    }, [dispatch, challanId]);

    if (loading) return <LoadingSpinner />;
    if (!challanDetail || !challanDetail.challan) return <div className="p-4 text-center">No Challan Found</div>;

    const { challan, items } = challanDetail;
    const transport = challan.transporter_details || {};
    const transportType = challan.transporter_type || "-";
    const normalizedTransportType = transportType.toLowerCase();

    // Transport flags
    const isSelfPickup = normalizedTransportType === "self pickup";
    const isCourier = normalizedTransportType === "courier";
    const isRoad = normalizedTransportType === "road";
    const isRail = normalizedTransportType === "rail";
    const isAir = normalizedTransportType === "air";

    const currencySymbol = challan.currency_symbol || "₹";

    const customerGstType = String(challan.customer_details?.gst_type ?? "").trim().toLowerCase();
    const isCompositionGst =
        customerGstType === "compositional" ||
        customerGstType === "compositional gst" ||
        customerGstType.includes("compositional");

    const splitSummary = {
        service: {
            sub_total: challan.split_summary?.service?.sub_total || 0,
            discount_value: challan.split_summary?.service?.discount_value || 0,
            net_value: challan.split_summary?.service?.net_value || 0,
            gst_value: isCompositionGst ? 0 : (challan.split_summary?.service?.gst_value || 0),
            total_amount: challan.split_summary?.service?.total_amount || 0,
        },
        accessories: {
            sub_total: challan.split_summary?.accessories?.sub_total || 0,
            discount_value: challan.split_summary?.accessories?.discount_value || 0,
            net_value: challan.split_summary?.accessories?.net_value || 0,
            gst_value: isCompositionGst ? 0 : (challan.split_summary?.accessories?.gst_value || 0),
            total_amount: challan.split_summary?.accessories?.total_amount || 0,
        },
    };
    const grandTotal = isCompositionGst
        ? (challan.total_summary?.net_taxable || 0)
        : (challan.total_summary?.grand_total || 0);

    type TermsGroup = { type: string; options: string[] };

    const normalizeTermsAndConditions = (raw: unknown): TermsGroup[] => {
        if (!raw) return [];

        // Case 1: Array
        if (Array.isArray(raw)) {
            // 1a) Tuple format: [type, options]
            if (raw.length > 0 && Array.isArray(raw[0])) {
                return (raw as unknown[]).flatMap((entry): TermsGroup[] => {
                    if (!Array.isArray(entry)) return [];
                    const [t, opts] = entry as [unknown, unknown];
                    const type = String(t ?? "").trim();
                    if (!type) return [];
                    const options = Array.isArray(opts)
                        ? (opts as unknown[]).map((x) => String(x ?? "").trim()).filter(Boolean)
                        : String(opts ?? "").split(/\r?\n|,/).map((x) => x.trim()).filter(Boolean);
                    return [{ type, options }];
                });
            }

            // 1b) Object format: { type, options } (options may be string[] or string)
            if (raw.length > 0 && typeof raw[0] === "object") {
                return (raw as any[]).flatMap((entry): TermsGroup[] => {
                    if (!entry || typeof entry !== "object") return [];
                    const type = String(entry.type ?? entry.title ?? "").trim();
                    if (!type) return [];
                    const optsRaw = entry.options ?? entry.items ?? entry.points ?? [];
                    const options = Array.isArray(optsRaw)
                        ? (optsRaw as unknown[]).map((x) => String(x ?? "").trim()).filter(Boolean)
                        : String(optsRaw ?? "").split(/\r?\n|,/).map((x) => x.trim()).filter(Boolean);
                    return [{ type, options }];
                });
            }

            // 1c) String list format: ["term1", "term2"]
            return (raw as unknown[])
                .map((x) => String(x ?? "").trim())
                .filter(Boolean)
                .map((term) => ({ type: "Terms", options: [term] }));
        }

        // Case 2: Object map format: { [type]: options }
        if (typeof raw === "object") {
            return Object.entries(raw as Record<string, unknown>).map(([key, value]) => {
                const type = String(key ?? "").trim();
                const options = Array.isArray(value)
                    ? (value as unknown[]).map((x) => String(x ?? "").trim()).filter(Boolean)
                    : String(value ?? "").split(/\r?\n|,/).map((x) => x.trim()).filter(Boolean);
                return { type, options };
            }).filter((g) => g.type && g.options.length > 0);
        }

        return [];
    };

    const termsGroups = normalizeTermsAndConditions(challan.terms_and_conditions);

    return (
        <div className="page-content">
            <Container fluid>
                <Row>
                    <Col lg={12}>
                        <Card>
                            <Card.Header>
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 mb-2">
                                    <div className="d-flex align-items-center gap-3">
                                        <Link to={`/sales_order/service/challan/${challan.sales_order_service_id}`} style={{ color: "inherit", textDecoration: "none" }}>
                                            <i className="bi bi-arrow-left" style={{ fontSize: "1.2rem", cursor: "pointer" }} />
                                        </Link>
                                        <h6 className="card-title mb-0">View Details</h6>
                                    </div>
                                    <div className="text-muted small">
                                        Challan &gt; <span style={{ color: "inherit" }}>View Details</span>
                                    </div>
                                </div>
                                <hr />
                            </Card.Header>

                            <Card.Body>
                                {/* Header Information */}
                                <div className="mb-4">
                                    <Row>
                                        <Col md={6}>
                                            <p className="mb-2"><strong>Challan No:</strong> {challan.challan_no || "-"}</p>
                                            <p className="mb-2"><strong>So No:</strong> {challan.sales_order_number || challan.sales_order_service?.sales_order_number || "-"}</p>
                                        </Col>
                                        <Col md={6}>
                                            <p className="mb-2"><strong>Challan Date:</strong> <FormattedDate date={challan.challan_date} /></p>
                                            <p className="mb-2"><strong>Initiated By:</strong> {challan.initiated_by || "-"}</p>
                                            {(challan.warehouse_name || challan.warehouse?.warehouse_name) && (
                                                <p className="mb-2">
                                                    <strong>Warehouse:</strong>{" "}
                                                    {challan.warehouse_name || challan.warehouse?.warehouse_name}
                                                </p>
                                            )}
                                        </Col>
                                    </Row>
                                </div>

                                <hr style={{ margin: "20px 0", borderTop: "1px solid #dee2e6" }} />

                                {/* Customer Details */}
                                <div className="mb-4">
                                    <h6 className="fw-bold mb-3" style={{ color: "#495057", fontSize: "0.9rem" }}>Customer Details</h6>
                                    <div className="p-4 rounded-3 mb-4" style={{ backgroundColor: MecordColors?.LightThemeBGColor }}>
                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <p><strong>Company Name :</strong> {challan.customer_details?.company_name || "-"}</p>
                                                <p><strong>Company Email :</strong> {challan.customer_details?.email || "-"}</p>
                                                <p><strong>Customer PO Number :</strong> {challan.customer_details?.customer_po_number || "-"}</p>
                                            </Col>
                                            <Col md={6}>
                                                <p><strong>Industry :</strong> {challan.customer_details?.industry || "-"}</p>
                                                <p><strong>Company Phone No. :</strong> {challan.customer_details?.phone_number || "-"}</p>
                                                <p><strong>PO Date :</strong> {challan.customer_details?.customer_po_date ? <FormattedDate date={challan.customer_details.customer_po_date} /> : "-"}</p>
                                            </Col>
                                        </Row>
                                        {challan.customer_details?.contact_details?.length > 0 && (
                                            <>
                                                <hr />
                                                <h6 className="fw-bold mb-3">Additional Contact Details</h6>
                                                <Row>
                                                    {challan.customer_details.contact_details.map((contact: any, idx: number) => (
                                                        <Col md={6} key={idx} className="mb-3">
                                                            <p><strong>Contact Name :</strong> {contact.name || "-"}</p>
                                                            <p><strong>Email :</strong> {contact.email || "-"}</p>
                                                            <p><strong>Phone Number :</strong> {contact.phone_number || "-"}</p>
                                                        </Col>
                                                    ))}
                                                </Row>
                                            </>
                                        )}
                                    </div>
                                </div>

                                <hr style={{ margin: "20px 0", borderTop: "1px solid #dee2e6" }} />

                                {/* Billing Details */}
                                <div className="mb-4">
                                    <h6 className="fw-bold mb-3" style={{ color: "#495057", fontSize: "0.9rem" }}>Billing Details</h6>
                                    <div className="p-4 rounded-3" style={{ backgroundColor: MecordColors?.LightThemeBGColor }}>
                                        <Row>
                                            <Col md={6}>
                                                <p className="fw-bold mb-1">Bill From :</p>
                                                {challan.bill_from ? (
                                                    <p className="mb-0">
                                                        {challan.bill_from.org_address}<br />
                                                        {`${challan.bill_from.org_city}, ${challan.bill_from.org_state}, ${challan.bill_from.org_country} - ${challan.bill_from.org_pincode}`}
                                                    </p>
                                                ) : <p className="text-muted mb-0">Not available</p>}
                                            </Col>
                                            <Col md={6}>
                                                <p className="fw-bold mb-1">Bill To :</p>
                                                {challan.bill_to ? (
                                                    <p className="mb-0">
                                                        {challan.bill_to.address}<br />
                                                        {`${challan.bill_to.city}, ${challan.bill_to.state}, ${challan.bill_to.country} - ${challan.bill_to.pin_code}`}
                                                    </p>
                                                ) : <p className="text-muted mb-0">Not available</p>}
                                            </Col>
                                        </Row>
                                    </div>
                                </div>

                                <hr style={{ margin: "20px 0", borderTop: "1px solid #dee2e6" }} />

                                {/* Transport Details */}
                                <div className="mb-4">
                                    <h6 className="fw-bold mb-3" style={{ color: "#495057", fontSize: "0.9rem" }}>
                                        {isSelfPickup ? "Self Pickup Details" : isCourier ? "Courier Details" : isRoad ? "Road Transport Details" : isRail ? "Rail Transport Details" : isAir ? "Air Transport Details" : "Transport Details"}
                                    </h6>
                                    <Row>
                                        <Col md={6}>
                                            <p className="mb-2"><strong>Transporter Type:</strong> {transportType}</p>
                                        </Col>
                                        <Col md={6}>
                                            <p className="mb-2"><strong>Remark:</strong> {transport.remark || transport.remarks || challan.note || "-"}</p>
                                        </Col>
                                        {isSelfPickup && (
                                            <>
                                                <Col md={6}><p className="mb-2"><strong>Person Name:</strong> {transport.person_name || "-"}</p></Col>
                                                <Col md={6}><p className="mb-2"><strong>Person Number:</strong> {transport.person_number || "-"}</p></Col>
                                            </>
                                        )}
                                        {isCourier && (
                                            <>
                                                <Col md={6}><p className="mb-2"><strong>Docket Number:</strong> {transport.docket_number || "-"}</p></Col>
                                                <Col md={6}><p className="mb-2"><strong>Company Name:</strong> {transport.company_name || "-"}</p></Col>
                                            </>
                                        )}
                                        {isRoad && (
                                            <>
                                                <Col md={6}><p className="mb-2"><strong>Transporter Name:</strong> {transport.transporter_name || "-"}</p></Col>
                                                <Col md={6}><p className="mb-2"><strong>Truck Number:</strong> {transport.truck_number || transport.vehicle_number || "-"}</p></Col>
                                                <Col md={6}><p className="mb-2"><strong>Driver Name:</strong> {transport.driver_name || "-"}</p></Col>
                                                <Col md={6}><p className="mb-2"><strong>Driver Mobile:</strong> {transport.driver_mobile || transport.driver_number || "-"}</p></Col>
                                            </>
                                        )}
                                        {isRail && (
                                            <>
                                                <Col md={6}><p className="mb-2"><strong>Carrier Name:</strong> {transport.carrier_name || "-"}</p></Col>
                                                <Col md={6}><p className="mb-2"><strong>Train Number:</strong> {transport.train_number || "-"}</p></Col>
                                                <Col md={6}><p className="mb-2"><strong>Booking Number:</strong> {transport.booking_number || "-"}</p></Col>
                                                <Col md={6}><p className="mb-2"><strong>Origin:</strong> {transport.origin || "-"}</p></Col>
                                            </>
                                        )}
                                        {isAir && (
                                            <>
                                                <Col md={6}><p className="mb-2"><strong>Carrier Name:</strong> {transport.carrier_name || "-"}</p></Col>
                                                <Col md={6}><p className="mb-2"><strong>Flight Number:</strong> {transport.flight_number || "-"}</p></Col>
                                                <Col md={6}><p className="mb-2"><strong>AWB Number:</strong> {transport.awb_number || "-"}</p></Col>
                                                <Col md={6}><p className="mb-2"><strong>Origin:</strong> {transport.origin || "-"}</p></Col>
                                            </>
                                        )}
                                    </Row>
                                </div>

                                <hr style={{ margin: "20px 0", borderTop: "1px solid #dee2e6" }} />

                                {/* Product Details Table */}
                                <div className="mb-4">
                                    <h6 className="fw-bold mb-3">Product Details</h6>
                                    <div className="table-responsive p-3" style={{ backgroundColor: MecordColors.ActiveBackground, borderRadius: "10px" }}>
                                        <Table style={{ minWidth: "2000px", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                                            <thead style={{ backgroundColor: MecordColors.LightGrayColor }}>
                                                <tr>
                                                    <th>Sr No.</th>
                                                    <th>Model</th>
                                                    <th>Product</th>
                                                    <th>Serial No.</th>
                                                    <th>Service Category</th>
                                                    <th>Service Method</th>
                                                    <th>Service Type</th>
                                                    <th>Execution Mode</th>
                                                    <th>SAC Code</th>
                                                    <th>Product Quantity</th>
                                                    <th>Service Price</th>
                                                    {/* <th>Rate in Currency</th> */}
                                                    <th>Gross Value</th>
                                                    <th>Discount/Flat</th>
                                                    <th>Discount Value</th>
                                                    <th>Net Value</th>
                                                    <th>GST (%)</th>
                                                    <th>SGST (%)</th>
                                                    <th>CGST (%)</th>
                                                    <th>IGST (%)</th>
                                                    {!isCompositionGst && <th>GST Value</th>}
                                                    <th>Total</th>
                                                    {/* <th>Service Address</th> */}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(items || []).filter((it: any) => it.in_this_challan).map((item: any, idx: number) => {
                                                    const discountType = item?.discount_type || "currency";
                                                    const discountValueInput = parseFloat(item?.discount_value || item?.discount_amount || 0);
                                                    const grossValue = parseFloat(item?.gross_value || item?.service_price || item?.item_amount || 0);
                                                    const discountCalc =
                                                        discountType === "%" || discountType === "percentage"
                                                            ? (grossValue * discountValueInput) / 100
                                                            : Math.min(discountValueInput, grossValue);

                                                    const addr = item?.service_address_details || item?.service_address;
                                                    const serviceAddress = addr
                                                        ? typeof addr === "string"
                                                            ? addr
                                                            : [
                                                                addr.address,
                                                                addr.city,
                                                                addr.state,
                                                                addr.country,
                                                                addr.pincode || addr.pin_code,
                                                                addr.phone ? `Phone: ${addr.phone}` : null,
                                                                addr.phone_number ? `Phone: ${addr.phone_number}` : null,
                                                            ]
                                                                .filter(Boolean)
                                                                .join(", ")
                                                        : "-";

                                                    return (
                                                        <React.Fragment key={item.sales_order_service_item_id || idx}>
                                                        <tr>
                                                            <td>{idx + 1}</td>
                                                            <td>{item?.model_name || item?.model || "-"}</td>
                                                            <td>
                                                                {item?.product_name || item?.name || "-"}
                                                                <br />
                                                                <small className="text-muted">SKU: {item?.product_sku || item?.product_code || "-"}</small>
                                                            </td>
                                                            <td>
                                                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                                    <span>{item.serial_number || item.serial_no || "-"}</span>
                                                                    {item.serial_number && item.serial_number !== "-" && (
                                                                        <InfoIcon
                                                                            style={{
                                                                                cursor: "pointer",
                                                                                color: MecordColors?.ThemeBGColor || "#007bff",
                                                                                fontSize: "18px",
                                                                            }}
                                                                            onClick={() => fetchProductHistory(item.serial_number)}
                                                                        />
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td>{item?.service_category || item?.service_category_name || "-"}</td>
                                                            <td>{item?.service_method || item?.service_method_name || "-"}</td>
                                                            <td>{item?.service_type || item?.service_type_name || "-"}</td>
                                                            <td>{item?.execution_mode || item?.execution_mode_name || "-"}</td>
                                                            <td>{item?.sac_code || item?.hsn_sac_code || "-"}</td>
                                                            <td>{item?.product_quantity || item?.quantity || 1}</td>
                                                            <td>
                                                                {currencySymbol}{formatIndianAmount(item?.service_price || item?.unit_price || 0)}
                                                            </td>
                                                            {/* <td>
                                                                {item?.rate_in_currency
                                                                    ? `${currencySymbol}${formatIndianAmount(item.rate_in_currency)}`
                                                                    : "-"}
                                                            </td> */}
                                                            <td>
                                                                {currencySymbol}{formatIndianAmount(grossValue)}
                                                            </td>
                                                            <td>
                                                                {discountType === "%" || discountType === "percentage"
                                                                    ? `${formatIndianAmount(discountValueInput)}%`
                                                                    : `${currencySymbol}${formatIndianAmount(discountValueInput)}`}
                                                            </td>
                                                            <td>
                                                                {currencySymbol} {formatIndianAmount(discountCalc)}
                                                            </td>
                                                            <td>
                                                                {currencySymbol}{formatIndianAmount(item?.total_service_net_amount || item?.net_taxable_amount || item?.net_value || (grossValue - discountCalc))}
                                                            </td>
                                                            <td>
                                                                {item?.gst_percentage ?? item?.gst_percent ?? "-"}
                                                                {(item?.gst_percentage || item?.gst_percent) ? "%" : ""}
                                                            </td>
                                                            <td>
                                                                {item?.sgst_percentage ?? item?.sgst_percent
                                                                    ? `${item?.sgst_percentage ?? item?.sgst_percent}%`
                                                                    : "-"}
                                                            </td>
                                                            <td>
                                                                {item?.cgst_percentage ?? item?.cgst_percent
                                                                    ? `${item?.cgst_percentage ?? item?.cgst_percent}%`
                                                                    : "-"}
                                                            </td>
                                                            <td>
                                                                {item?.igst_percentage ?? item?.igst_percent
                                                                    ? `${item?.igst_percentage ?? item?.igst_percent}%`
                                                                    : "-"}
                                                            </td>
                                                            {!isCompositionGst && (
                                                                <td>
                                                                    {currencySymbol}{formatIndianAmount(item?.gst_value || item?.gst_amount || 0)}
                                                                </td>
                                                            )}
                                                            <td>
                                                                {currencySymbol}{formatIndianAmount(item?.total_service_amount || item?.total || item?.total_amount || 0)}
                                                            </td>
                                                            {/* <td style={{ maxWidth: 260, whiteSpace: "normal" }}>
                                                                {serviceAddress}
                                                            </td> */}
                                                        </tr>
                                                        {(Array.isArray(item.accessories) ? item.accessories : []).flatMap(
                                                            (acc: any, accIdx: number) => {
                                                                const qty = Math.max(0, Number(acc.quantity || 0));
                                                                const serials: string[] = Array.isArray(acc.serial_numbers)
                                                                    ? acc.serial_numbers
                                                                    : [];
                                                                const unitCount = Math.max(qty, serials.length, 1);
                                                                return Array.from({ length: unitCount }, (_, unitIdx) => {
                                                                    const serial =
                                                                        serials[unitIdx] ||
                                                                        (qty === 1 && serials[0]) ||
                                                                        "-";
                                                                    const isFirst = unitIdx === 0;
                                                                    return (
                                                                <tr key={`${item.sales_order_service_item_id}-acc-${accIdx}-u-${unitIdx}`} style={{ backgroundColor: "#f8fafc" }}>
                                                                    <td>{idx + 1}.{accIdx + 1}{unitCount > 1 ? `.${unitIdx + 1}` : ""}</td>
                                                                    <td>{acc.model_name || "-"}</td>
                                                                    <td>
                                                                        <span
                                                                            style={{
                                                                                display: "inline-block",
                                                                                marginRight: 6,
                                                                                padding: "1px 6px",
                                                                                borderRadius: 999,
                                                                                backgroundColor: MecordColors.LightThemeBGColor,
                                                                                color: MecordColors.ThemeBGColor,
                                                                                fontSize: 10,
                                                                                fontWeight: 700,
                                                                            }}
                                                                        >
                                                                            Accessory
                                                                        </span>
                                                                        {acc.product_name || "-"}
                                                                    </td>
                                                                    <td>{serial || "-"}</td>
                                                                    <td>-</td>
                                                                    <td>-</td>
                                                                    <td>-</td>
                                                                    <td>-</td>
                                                                    <td>{acc.hsn_code || acc.sac_code || "-"}</td>
                                                                    <td>1</td>
                                                                    <td>{isFirst ? `${currencySymbol}${formatIndianAmount(acc.unit_price || 0)}` : "-"}</td>
                                                                    <td>{isFirst ? `${currencySymbol}${formatIndianAmount(acc.gross_value || 0)}` : "-"}</td>
                                                                    <td>
                                                                        {isFirst
                                                                            ? (acc.discount_type === "percentage" || acc.discount_type === "%"
                                                                                ? `${formatIndianAmount(acc.discount_value || 0)}%`
                                                                                : `${currencySymbol}${formatIndianAmount(acc.discount_value || 0)}`)
                                                                            : "-"}
                                                                    </td>
                                                                    <td>{isFirst ? `${currencySymbol}${formatIndianAmount(acc.discount_amount || 0)}` : "-"}</td>
                                                                    <td>{isFirst ? `${currencySymbol}${formatIndianAmount(acc.taxable_amount || 0)}` : "-"}</td>
                                                                    <td>{isFirst ? (acc.gst_percentage ?? "-") : "-"}</td>
                                                                    <td>{isFirst ? (acc.sgst_percentage ? `${acc.sgst_percentage}%` : "-") : "-"}</td>
                                                                    <td>{isFirst ? (acc.cgst_percentage ? `${acc.cgst_percentage}%` : "-") : "-"}</td>
                                                                    <td>{isFirst ? (acc.igst_percentage ? `${acc.igst_percentage}%` : "-") : "-"}</td>
                                                                    {!isCompositionGst && (
                                                                        <td>{isFirst ? `${currencySymbol}${formatIndianAmount(acc.gst_value || 0)}` : "-"}</td>
                                                                    )}
                                                                    <td>{isFirst ? `${currencySymbol}${formatIndianAmount(acc.total_amount || 0)}` : "-"}</td>
                                                                </tr>
                                                                    );
                                                                });
                                                            },
                                                        )}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                </div>

                                {/* Terms & Summary */}
                                <Row className="mb-4 mt-4">
                                    <Col md={6}>
                                        {termsGroups.length > 0 && (
                                            <div className="mb-4">
                                                <h5 className="mb-3 fw-bold">Terms &amp; Conditions</h5>
                                                <div className="border rounded-3 overflow-hidden bg-white" style={{ borderColor: "#e9ebec" }}>
                                                    {termsGroups.map((group, index, arr) => (
                                                        <React.Fragment key={`${group.type}-${index}`}>
                                                            <div className="p-2">
                                                                <h6 className="fw-bold fs-14 mb-2">{group.type}</h6>
                                                                <ul className="text-muted fs-13 ps-3 mb-0" style={{ listStyleType: "disc" }}>
                                                                    {group.options.map((opt, idx) => (
                                                                        <li key={`${group.type}-${index}-${idx}`} className="mb-1">
                                                                            {opt}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                            {index !== arr.length - 1 && (
                                                                <hr className="my-0 mx-2" style={{ borderTop: "1px solid #e9ebec", opacity: 1 }} />
                                                            )}
                                                        </React.Fragment>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </Col>
                                    <Col md={6}>
                                        <ChallanSplitSummary
                                            currencySymbol={currencySymbol}
                                            splitSummary={splitSummary}
                                            grandTotal={grandTotal}
                                            isCompositionGst={isCompositionGst}
                                        />
                                    </Col>
                                </Row>

                                {/* Notes & Attachments */}
                                <div className="mb-4">
                                    <h6 className="fw-bold mb-3">Notes & Attachments</h6>
                                    <Table bordered responsive>
                                        <tbody>
                                            <tr>
                                                <td style={{ width: "20%" }}><strong>Notes</strong></td>
                                                <td>{challan.note || "-"}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Attachments</strong></td>
                                                <td>
                                                    {(challan.attachments || []).length > 0 ? (
                                                        challan.attachments.map((file: any, index: number) => (
                                                            <div key={index} className="mb-1">
                                                                <a href={file} target="_blank" rel="noreferrer" className="text-decoration-underline">
                                                                    Attachment {index + 1}
                                                                </a>
                                                            </div>
                                                        ))
                                                    ) : "-"}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </div>

                                <div className="text-center mt-4 pt-3">
                                    <Button
                                        variant="outline-secondary"
                                        className="px-5 rounded-pill"
                                        onClick={() => navigate(`/sales_order/service/challan/${challan.sales_order_service_id}`)}
                                    >
                                        Back
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
            {/* Product History Modal */}
            <ProductHistoryModal
                show={showHistoryModal}
                onHide={() => {
                    setShowHistoryModal(false);
                    setProductHistory(null);
                    setSelectedSerialNo("");
                }}
                serialNo={selectedSerialNo}
                productHistory={productHistory}
                loading={loadingHistory}
            />
        </div>
    );
};

export default ViewChallan;
